import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation, useParams, Link } from 'react-router-dom';
import {
  Button, CircularProgress, FormControlLabel, Grid, IconButton, MenuItem, Paper,
  Radio, RadioGroup, TextField, Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close } from '@material-ui/icons';
import { showNotification } from 'store/actions/notifications';
import { NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'constants/notification';
import { UserContext } from 'contexts/user';
import * as api from './api';
import Etiqueta from './Etiqueta';

// Proposta dinâmica (24/10/2026). Aberta pelo campo "Link proposta" do
// Pipedrive: /propostas/nova?negocio=<dealId>. Edição: /propostas/:id/editar
// (nova versão no mesmo link). A conta é sempre da api (/propostas/calcular);
// a prévia à direita é a própria página do cliente (cj-proposta /preview).

const useStyles = makeStyles((theme) => ({
  pagina: { maxWidth: 1240, margin: '0 auto', padding: '20px 16px 64px' },
  grade: { display: 'grid', gridTemplateColumns: '1fr', gap: 20, [theme.breakpoints.up('md')]: { gridTemplateColumns: 'minmax(0,1fr) 380px', alignItems: 'start' } },
  card: { padding: 16, borderRadius: 12, marginBottom: 14, border: '1px solid #E4E3E9' },
  tituloCard: { fontSize: 13, letterSpacing: '.1em', textTransform: 'uppercase', color: '#1F9833', fontWeight: 900, margin: '0 0 10px' },
  dica: { fontSize: 12.5, color: '#64688B', margin: '8px 0 0' },
  req: { display: 'grid', gridTemplateColumns: 'minmax(0,1.1fr) minmax(0,.7fr) minmax(0,1.6fr) minmax(0,1fr) auto', gap: 8, alignItems: 'end', paddingBottom: 6, borderBottom: '1px dashed #E4E3E9', marginBottom: 8, [theme.breakpoints.down('sm')]: { gridTemplateColumns: '1fr 1fr' } },
  taxa: { gridColumn: '1/-1', fontSize: 12, color: '#64688B' },
  planos: { display: 'grid', gridTemplateColumns: '1fr', gap: 8, [theme.breakpoints.up('sm')]: { gridTemplateColumns: '1fr 1fr 1fr' } },
  plano: { border: '1px solid #E4E3E9', borderRadius: 10, padding: '8px 10px', margin: 0, alignItems: 'flex-start' },
  planoAtivo: { borderColor: '#1F9833', background: '#E8F5EA' },
  totais: { display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12, fontSize: 14, fontVariantNumeric: 'tabular-nums' },
  linha: { display: 'flex', justifyContent: 'space-between', gap: 12 },
  grande: { fontSize: 18, fontWeight: 900, borderTop: '1px solid #E4E3E9', paddingTop: 6, marginTop: 4 },
  secao: { marginTop: 10, fontSize: 12, letterSpacing: '.08em', textTransform: 'uppercase', color: '#64688B', fontWeight: 700 },
  acoes: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 14, alignItems: 'center' },
  linkbox: { marginTop: 12, background: '#E8F5EA', borderRadius: 10, padding: 12 },
  codigo: { display: 'block', fontFamily: 'ui-monospace,Menlo,monospace', fontSize: 13, wordBreak: 'break-all', background: '#fff', padding: '8px 10px', borderRadius: 6, margin: '6px 0 10px', border: '1px solid #E4E3E9' },
  previa: { position: 'sticky', top: 80 },
  celular: { margin: '0 auto', width: '100%', maxWidth: 360, background: '#fff', border: '8px solid #22252E', borderRadius: 32, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,.18)', height: 680 },
  aviso: { background: '#FFF4DD', borderRadius: 8, padding: '8px 12px', fontSize: 13.5, marginTop: 10 },
}));

const novoRequerente = (produtoId) => ({ nome: '', menor: false, produto_id: produtoId || '', depende_de: null });

function NovaProposta({ notify }) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();
  const { id } = useParams();
  const { user } = React.useContext(UserContext);
  const iframe = useRef(null);
  const ultimaPagina = useRef(null);

  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const [cat, setCat] = useState(null);
  const [deal, setDeal] = useState(null);
  const [existentes, setExistentes] = useState([]);
  const [analise, setAnalise] = useState(null);
  const [proposta, setProposta] = useState(null);
  const [dealDigitado, setDealDigitado] = useState('');

  const [plano, setPlano] = useState('2026');
  const [requerentes, setRequerentes] = useState([]);
  const [desconto, setDesconto] = useState('0');
  const [parcelas, setParcelas] = useState(12);
  const [validade, setValidade] = useState(7);
  const [observacoes, setObservacoes] = useState('');
  const [valorPago, setValorPago] = useState('');
  const [dataLaudo, setDataLaudo] = useState('');

  const [calculo, setCalculo] = useState(null);
  const [erros, setErros] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [copiado, setCopiado] = useState('');

  const dealId = useMemo(() => new URLSearchParams(location.search).get('negocio'), [location.search]);
  const produtos = (cat && cat.produtos) || [];
  const produtoDe = (pid) => produtos.find((p) => p.id === Number(pid));

  // Carga: catálogo + negócio (nova) ou proposta (edição).
  useEffect(() => {
    let vivo = true;
    (async () => {
      setCarregando(true);
      setErroCarga('');
      try {
        const c = await api.catalogo();
        if (!vivo) return;
        setCat(c);
        const padrao = (c.produtos.find((p) => /simples/i.test(p.titulo)) || c.produtos[0] || {}).id;
        if (id) {
          const d = await api.detalhe(id);
          if (!vivo) return;
          aplicarDetalhe(d);
        } else if (dealId) {
          const n = await api.negocio(dealId);
          if (!vivo) return;
          setDeal(n.deal);
          setExistentes(n.propostas || []);
          setAnalise(n.analise);
          setRequerentes([{ ...novoRequerente(padrao), nome: ((n.deal.person && n.deal.person.name) || '').split(' ')[0] }]);
        } else {
          setRequerentes([novoRequerente(padrao)]);
        }
      } catch (e) {
        if (vivo) setErroCarga(api.mensagemDoErro(e));
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
    // eslint-disable-next-line
  }, [id, dealId]);

  const aplicarDetalhe = (d) => {
    const p = d.proposta;
    setProposta(p);
    setDeal({ deal_id: p.negocio, title: p.deal_title, pt: p.pt, person: { name: p.cliente, email: p.client_email, phone: p.client_phone } });
    const inp = d.versao_atual && d.versao_atual.input;
    if (inp) {
      setPlano(inp.plano);
      setRequerentes(inp.requerentes.map((r) => ({ ...r, depende_de: r.depende_de === undefined ? null : r.depende_de })));
      setDesconto(String(inp.desconto_pct || 0));
      setParcelas(inp.parcelas);
      setValidade(inp.validade_dias);
      setObservacoes(inp.observacoes || '');
      setValorPago(inp.continuacao && inp.continuacao.valor_pago_centavos ? String(inp.continuacao.valor_pago_centavos / 100) : '');
      setDataLaudo((inp.continuacao && inp.continuacao.data_laudo) || '');
    }
  };

  // Continuação: pré-preenche com a venda do Plano Análise do mesmo PT.
  useEffect(() => {
    if (plano === 'continuacao' && analise && !valorPago && !dataLaudo) {
      setValorPago(String((analise.valor_pago_centavos || 0) / 100));
      setDataLaudo(analise.data_laudo || '');
    }
    // eslint-disable-next-line
  }, [plano, analise]);

  const corpo = useCallback(() => ({
    plano,
    requerentes: requerentes.map((r) => ({ ...r, produto_id: Number(r.produto_id) || 0 })),
    desconto_pct: String(desconto).replace(',', '.') || 0,
    parcelas,
    validade_dias: validade,
    observacoes,
    continuacao: {
      valor_pago_centavos: Math.round((parseFloat(String(valorPago).replace(/\./g, '').replace(',', '.')) || 0) * 100),
      data_laudo: dataLaudo || null,
    },
    cliente_nome: deal && deal.person ? deal.person.name : null,
  }), [plano, requerentes, desconto, parcelas, validade, observacoes, valorPago, dataLaudo, deal]);

  const enviarPrevia = useCallback((pagina) => {
    ultimaPagina.current = pagina;
    if (iframe.current && iframe.current.contentWindow && pagina && cat) {
      iframe.current.contentWindow.postMessage({ type: 'cjp-previa', pagina }, cat.url_publica);
    }
  }, [cat]);

  // Conta ao vivo (api), com espera curta enquanto digita.
  useEffect(() => {
    if (!cat || !requerentes.length) return undefined;
    const t = setTimeout(async () => {
      try {
        const r = await api.calcular(corpo());
        setCalculo(r.calculo);
        setErros(r.erros || {});
        enviarPrevia(r.pagina);
      } catch (e) { /* mantém a última conta */ }
    }, 350);
    return () => clearTimeout(t);
  }, [cat, requerentes, corpo, enviarPrevia]);

  // O iframe avisa quando está pronto: manda a última prévia.
  useEffect(() => {
    const aoReceber = (e) => {
      if (cat && e.origin === cat.url_publica && e.data && e.data.type === 'cjp-pronto' && ultimaPagina.current) {
        enviarPrevia(ultimaPagina.current);
      }
    };
    window.addEventListener('message', aoReceber);
    return () => window.removeEventListener('message', aoReceber);
  }, [cat, enviarPrevia]);

  const mudarReq = (i, campo, valor) => {
    setRequerentes((lista) => lista.map((r, j) => {
      if (j !== i) return r;
      const novo = { ...r, [campo]: valor };
      if (campo === 'produto_id') {
        const p = produtoDe(valor);
        if (p && p.menor) novo.menor = true;
      }
      return novo;
    }));
  };
  const removerReq = (i) => setRequerentes((lista) => lista
    .filter((_, j) => j !== i)
    .map((r) => {
      if (r.depende_de === i) return { ...r, depende_de: null };
      if (r.depende_de !== null && r.depende_de > i) return { ...r, depende_de: r.depende_de - 1 };
      return r;
    }));
  const nomeDe = (r, i) => (r.nome || '').trim() || `Requerente ${i + 1}`;

  const salvar = async (gerarLink) => {
    if (!deal) {
      notify(NOTIFICATION_TYPE_ERROR, 'Abra a proposta a partir do número do negócio.');
      return;
    }
    setSalvando(true);
    try {
      const body = { ...corpo(), gerar: gerarLink };
      const r = proposta
        ? await api.salvarVersao(proposta.id, body)
        : await api.criar({ ...body, deal_id: deal.deal_id });
      aplicarDetalhe(r);
      notify(NOTIFICATION_TYPE_SUCCESS, r.message || 'Salvo');
      if (!proposta) history.replace(`/propostas/${r.proposta.id}/editar`);
    } catch (e) {
      notify(NOTIFICATION_TYPE_ERROR, api.mensagemDoErro(e));
    } finally {
      setSalvando(false);
    }
  };

  const aprovarDesconto = async () => {
    setSalvando(true);
    try {
      aplicarDetalhe(await api.aprovar(proposta.id));
      notify(NOTIFICATION_TYPE_SUCCESS, 'Desconto aprovado e link publicado');
    } catch (e) {
      notify(NOTIFICATION_TYPE_ERROR, api.mensagemDoErro(e));
    } finally {
      setSalvando(false);
    }
  };

  const copiar = (texto, rotulo) => {
    const ok = () => { setCopiado(`${rotulo} copiado.`); setTimeout(() => setCopiado(''), 2500); };
    if (navigator.clipboard) navigator.clipboard.writeText(texto).then(ok).catch(() => setCopiado('Selecione e copie o texto acima.'));
  };

  if (carregando) {
    return <div className={classes.pagina}><CircularProgress size={28} /> Carregando…</div>;
  }

  if (!deal && !id) {
    return (
      <div className={classes.pagina}>
        <Typography variant="h5" style={{ fontWeight: 900 }}>Nova proposta</Typography>
        {erroCarga && <p style={{ color: '#DB0A3C' }}>{erroCarga}</p>}
        <Paper className={classes.card} elevation={0}>
          <h3 className={classes.tituloCard}>Negócio</h3>
          <form onSubmit={(e) => { e.preventDefault(); const n = dealDigitado.replace(/\D/g, ''); if (n) history.push(`/propostas/nova?negocio=${n}`); }}>
            <Grid container spacing={2} alignItems="flex-end">
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Número do negócio (ID do Pipedrive)" value={dealDigitado} onChange={(e) => setDealDigitado(e.target.value)} inputProps={{ inputMode: 'numeric' }} />
              </Grid>
              <Grid item><Button type="submit" variant="contained" color="primary">Buscar</Button></Grid>
            </Grid>
          </form>
          <p className={classes.dica}>No Pipedrive, o campo "Link proposta" do negócio abre esta tela já com o negócio carregado.</p>
        </Paper>
      </div>
    );
  }

  if (erroCarga) {
    return (
      <div className={classes.pagina}>
        <Typography variant="h5" style={{ fontWeight: 900 }}>Nova proposta</Typography>
        <p style={{ color: '#DB0A3C' }}>{erroCarga}</p>
        <Button component={Link} to="/propostas/nova">Tentar outro negócio</Button>
      </div>
    );
  }

  const h = calculo && calculo.honorarios;
  const etapas = calculo ? calculo.linha_do_tempo.filter((e) => e.codigo !== 'assinatura') : [];
  const max = cat ? cat.desconto_max_vendedor : 5;
  const acimaDaAlcada = Number(String(desconto).replace(',', '.')) > max;
  const coordenador = cat && cat.coordenador;
  const link = proposta && proposta.link;

  return (
    <div className={classes.pagina}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <Typography variant="h5" style={{ fontWeight: 900 }}>
          {proposta ? `Proposta nº ${proposta.numero}` : 'Nova proposta'}
        </Typography>
        {proposta && <Etiqueta status={proposta.status} />}
        {proposta && <span style={{ color: '#64688B', fontSize: 13 }}>versão {proposta.versao_atual}{proposta.versao_publicada ? ` · cliente vê a versão ${proposta.versao_publicada}` : ''}</span>}
        <Link to="/propostas" style={{ marginLeft: 'auto', fontSize: 14 }}>Minhas propostas</Link>
      </div>
      <p style={{ color: '#64688B', margin: '4px 0 16px', maxWidth: '75ch' }}>
        Honorários pela regra do plano; certidões e vale postal ficam fora e aparecem numa linha do tempo para o cliente.
      </p>

      <div className={classes.grade}>
        <div>
          <Paper className={classes.card} elevation={0}>
            <h3 className={classes.tituloCard}>Negócio</h3>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}><TextField fullWidth label="Número do negócio" value={deal ? deal.deal_id : ''} InputProps={{ readOnly: true }} /></Grid>
              <Grid item xs={12} sm={8}><TextField fullWidth label="Nome do cliente (Pipedrive)" value={(deal && deal.person && deal.person.name) || ''} InputProps={{ readOnly: true }} /></Grid>
            </Grid>
            {!proposta && existentes.length > 0 && (
              <div className={classes.aviso}>
                Este negócio já tem {existentes.length === 1 ? 'a proposta' : 'as propostas'}{' '}
                {existentes.map((p, i) => (
                  <span key={p.id}>{i ? ', ' : ''}<Link to={`/propostas/${p.id}/editar`}>nº {p.numero}</Link> ({(api.STATUS[p.status] || {}).rotulo})</span>
                ))}
                . Para mudar valores, abra a existente: editar cria uma nova versão no mesmo link.
              </div>
            )}
          </Paper>

          <Paper className={classes.card} elevation={0}>
            <h3 className={classes.tituloCard}>Requerentes</h3>
            {requerentes.map((r, i) => {
              const p = produtoDe(r.produto_id);
              const dep = r.depende_de !== null && r.depende_de !== undefined ? requerentes[r.depende_de] : null;
              return (
                <div className={classes.req} key={i}>
                  <TextField label="Nome (opcional)" placeholder={`Requerente ${i + 1}`} value={r.nome} onChange={(e) => mudarReq(i, 'nome', e.target.value)} />
                  <TextField select label="Idade" value={r.menor ? '1' : '0'} onChange={(e) => mudarReq(i, 'menor', e.target.value === '1')}>
                    <MenuItem value="0">Adulto</MenuItem>
                    <MenuItem value="1">Menor</MenuItem>
                  </TextField>
                  <TextField select label="Processo" value={r.produto_id} onChange={(e) => mudarReq(i, 'produto_id', e.target.value)} error={!!erros[`requerentes.${i}.produto_id`]}>
                    {produtos.map((q) => <MenuItem key={q.id} value={q.id}>{q.titulo}</MenuItem>)}
                  </TextField>
                  <TextField select label="Depende de" value={r.depende_de === null || r.depende_de === undefined ? '' : r.depende_de} onChange={(e) => mudarReq(i, 'depende_de', e.target.value === '' ? null : Number(e.target.value))}>
                    <MenuItem value="">—</MenuItem>
                    {requerentes.map((o, j) => (j === i ? null : <MenuItem key={j} value={j}>{nomeDe(o, j)}</MenuItem>))}
                  </TextField>
                  <IconButton aria-label="Remover requerente" onClick={() => removerReq(i)} disabled={requerentes.length === 1} size="small"><Close /></IconButton>
                  <div className={classes.taxa}>
                    {p ? `Taxas deste processo: certidões ${api.brl(p.certidoes)} · vale postal ${api.brl(p.vale_postal)}${p.outras ? ` · outras taxas ${api.brl(p.outras)}` : ''}` : 'Escolha o processo'}
                    {dep ? <b style={{ color: '#9A6700' }}>{` · futuro, após ${nomeDe(dep, r.depende_de)}`}</b> : null}
                    {p && p.transcricao ? ' · transcrição do próprio português: não conta como adulto' : ''}
                  </div>
                </div>
              );
            })}
            <div className={classes.acoes}>
              <Button size="small" variant="outlined" onClick={() => setRequerentes((l) => [...l, novoRequerente((produtos.find((p) => /simples/i.test(p.titulo)) || produtos[0] || {}).id)])}>+ Adicionar requerente</Button>
            </div>
            <p className={classes.dica}>Nome é opcional; o mesmo nome em duas linhas conta como a mesma pessoa. "Depende de" marca o processo que só começa depois que outro conclui: conta nos honorários, mas as taxas vão para a etapa "Futuro".</p>
          </Paper>

          <Paper className={classes.card} elevation={0}>
            <h3 className={classes.tituloCard}>Plano</h3>
            <RadioGroup value={plano} onChange={(e) => setPlano(e.target.value)} className={classes.planos}>
              <FormControlLabel className={`${classes.plano} ${plano === '2026' ? classes.planoAtivo : ''}`} value="2026" control={<Radio color="primary" />} label={<span><b>Plano 2026</b><br /><small>Tabela por adultos; menor paga metade da média. Certidões e vale postal fora.</small></span>} />
              <FormControlLabel className={`${classes.plano} ${plano === 'analise' ? classes.planoAtivo : ''}`} value="analise" control={<Radio color="primary" />} label={<span><b>Plano Análise</b><br /><small>R$ 1.000 + R$ 500 por requerente adicional. Só certidões.</small></span>} />
              <FormControlLabel className={`${classes.plano} ${plano === 'continuacao' ? classes.planoAtivo : ''}`} value="continuacao" control={<Radio color="primary" />} label={<span><b>2026 – continuação</b><br /><small>Tabela do 2026 com abatimento da análise (laudo com menos de 12 meses). Só vale postal.</small></span>} />
            </RadioGroup>
            {plano === 'continuacao' && (
              <Grid container spacing={2} style={{ marginTop: 6 }}>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Valor pago na análise (R$)" value={valorPago} onChange={(e) => setValorPago(e.target.value)} inputProps={{ inputMode: 'decimal' }} /></Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth type="date" label="Data do laudo" InputLabelProps={{ shrink: true }} value={dataLaudo} onChange={(e) => setDataLaudo(e.target.value)} helperText="Abate só se o laudo tiver menos de 12 meses" /></Grid>
              </Grid>
            )}
          </Paper>

          <Paper className={classes.card} elevation={0}>
            <h3 className={classes.tituloCard}>Honorários, parcelas e validade</h3>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth label="Desconto (%)" value={desconto} onChange={(e) => setDesconto(e.target.value)} inputProps={{ inputMode: 'decimal' }} helperText={`Sua alçada: até ${String(max).replace('.', ',')}%`} error={!!erros.desconto_pct} />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField select fullWidth label="Parcelamento dos honorários" value={parcelas} onChange={(e) => setParcelas(Number(e.target.value))}>
                  <MenuItem value={1}>À vista (Pix ou boleto)</MenuItem>
                  <MenuItem value={6}>6x sem juros no cartão</MenuItem>
                  <MenuItem value={12}>12x sem juros no cartão</MenuItem>
                  <MenuItem value={18}>18x no cartão</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField fullWidth type="number" label="Validade (dias)" value={validade} onChange={(e) => setValidade(Math.max(1, Math.min(60, Number(e.target.value) || 7)))} helperText="Padrão: 7 dias" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={2} label="Observações para o cliente" value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
              </Grid>
            </Grid>

            {h && (
              <div className={classes.totais}>
                <div className={classes.linha}><span>Honorários do plano <small style={{ color: '#64688B' }}>({resumoPlano(calculo)})</small></span><span>{api.brl(h.tabela)}</span></div>
                <div className={classes.linha}><span>Desconto {String(h.desconto_pct).replace('.', ',')}%</span><span>−{api.brl(h.desconto)}</span></div>
                {plano === 'continuacao' && (
                  <div className={classes.linha}>
                    <span>Abatimento da análise {h.abatimento_aplicavel === false ? <small style={{ color: '#DB0A3C' }}>(laudo com mais de 12 meses ou sem data: não abate)</small> : null}</span>
                    <span>−{api.brl(h.abatimento)}</span>
                  </div>
                )}
                <div className={`${classes.linha} ${classes.grande}`}><span>Honorários a pagar</span><span>{api.brl(h.total)}</span></div>
                <div className={classes.linha}><span>Parcela</span><span>{h.parcelas > 1 ? `${h.parcelas}x de ${api.brl2(h.parcela)}` : 'à vista'}</span></div>
                <div className={`${classes.linha} ${classes.secao}`}>Taxas fora do plano (cliente paga por etapa)</div>
                {etapas.map((e) => (
                  <div className={classes.linha} key={e.codigo} style={e.codigo === 'futuro' ? { color: '#9A6700' } : null}>
                    <span>{e.titulo}{e.codigo === 'futuro' ? ' · após a conclusão de outro requerente' : ''}</span><span>{api.brl(e.total)}</span>
                  </div>
                ))}
              </div>
            )}
            {acimaDaAlcada && !coordenador && (
              <div className={classes.aviso}>Desconto acima de {String(max).replace('.', ',')}%: ao gerar, a proposta vai para aprovação do coordenador antes de o link mostrar estes valores.</div>
            )}

            <div className={classes.acoes}>
              <Button variant="contained" color="primary" disabled={salvando} onClick={() => salvar(true)}>
                {proposta && proposta.versao_publicada ? 'Salvar nova versão e publicar' : 'Gerar link'}
              </Button>
              <Button variant="outlined" disabled={salvando} onClick={() => salvar(false)}>Salvar rascunho</Button>
              {proposta && proposta.status === 'aguardando_aprovacao' && coordenador && (
                <Button variant="contained" style={{ background: '#EE9E00', color: '#14142A' }} disabled={salvando} onClick={aprovarDesconto}>Aprovar desconto</Button>
              )}
              {salvando && <CircularProgress size={20} />}
              <span style={{ color: '#1F9833', fontWeight: 700, fontSize: 13 }} aria-live="polite">{copiado}</span>
            </div>

            {proposta && proposta.status === 'aguardando_aprovacao' && (
              <div className={classes.aviso}>Aguardando aprovação do desconto por um coordenador.{proposta.versao_publicada ? ' Enquanto isso, o link mostra a versão anterior.' : ' O link só abre depois da aprovação.'}</div>
            )}
            {link && (
              <div className={classes.linkbox}>
                <strong>Link único da proposta</strong>
                <code className={classes.codigo}>{link}</code>
                <div className={classes.acoes} style={{ marginTop: 0 }}>
                  <Button size="small" variant="outlined" onClick={() => copiar(link, 'Link')}>Copiar link</Button>
                  <Button size="small" variant="outlined" onClick={() => copiar(api.mensagemWhatsapp({ cliente: deal.person && deal.person.name, vendedor: user && user.name && user.name.split(' ')[0], calculo: (calculo || {}), link }), 'Mensagem')}>Copiar mensagem do WhatsApp</Button>
                  <Button size="small" variant="outlined" href={`${link}#interno`} target="_blank" rel="noopener">Abrir como o cliente</Button>
                </div>
                <p className={classes.dica}>"Abrir como o cliente" não conta como abertura. Para o PDF, abra e use "Ver como PDF" no fim da proposta (ou imprimir → salvar como PDF).</p>
              </div>
            )}
          </Paper>
        </div>

        <aside className={classes.previa} aria-label="Prévia da proposta no celular">
          <div className={classes.celular}>
            {cat && <iframe ref={iframe} title="Prévia da proposta" src={`${cat.url_publica}/preview`} style={{ width: '100%', height: '100%', border: 0 }} onLoad={() => ultimaPagina.current && enviarPrevia(ultimaPagina.current)} />}
          </div>
          <p style={{ textAlign: 'center', color: '#64688B', fontSize: 12, margin: '8px 0 0' }}>Prévia ao vivo: é a página que o cliente abre.</p>
        </aside>
      </div>
    </div>
  );
}

function resumoPlano(c) {
  if (!c) return '';
  if (c.plano === 'analise') {
    const n = c.resumo.n_requerentes || 0;
    return `R$ 1.000${n > 1 ? ` + ${n - 1}×R$ 500` : ''}`;
  }
  if (c.resumo.modo === 'estado_civil') return `${c.resumo.n_adultos} pessoa(s) × R$ 3.000, só estado civil`;
  const a = c.resumo.n_adultos || 0;
  return `${a} adulto${a !== 1 ? 's' : ''} pela tabela${c.resumo.n_menores ? ` + ${c.resumo.n_menores}×${api.brl(c.resumo.valor_por_menor)} (menor)` : ''}`;
}

export default connect(null, { notify: showNotification })(NovaProposta);
